import { collection, getDocs, doc, getDoc, addDoc, updateDoc, deleteDoc, Timestamp, writeBatch, increment } from 'firebase/firestore';
import { db } from '../firebase/firebase';

export interface UserModel {
  userId: string;
  phone: string;
  fcmToken: string;
  notactive: boolean;
  walletBalance: number;
  username?: string | null;
  createdTimestamp: Timestamp; // Firestore timestamp
  // Fields kept in sync with the Flutter customer app (CurrentUser model):
  orderCount?: number;            // incremented (FieldValue.increment) on each placed order
  profilePicUrl?: string | null;  // Google/photo URL
  notificationsEnabled?: boolean;
  whatsappEnabled?: boolean;
  // "Last seen" — written by the customer app on each app open / login.
  // Value may be a Firestore Timestamp, ISO string, or epoch number.
  lastOpened?: any;
  lastOpenedAt?: any;
  lastActive?: any;
}

const usersCollection = collection(db, 'users');

// Create new user
export const createUser = async (userData: Omit<UserModel, 'createdTimestamp'>) => {
  try {
    const dataWithTimestamp = {
      ...userData,
      createdTimestamp: Timestamp.now(),
    };
    const docRef = await addDoc(usersCollection, dataWithTimestamp);
    return docRef.id;
  } catch (error) {
    console.error('Failed to create user:', error);
    throw error;
  }
};

// Get user by ID (document ID)
export const getUser = async (userId: string): Promise<UserModel | null> => {
  try {
    const docRef = doc(usersCollection, userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { ...(docSnap.data() as UserModel), userId: docSnap.id };
    } else {
      return null;
    }
  } catch (error) {
    console.error('Failed to get user:', error);
    return null;
  }
};

// Update user fields
export const updateUser = async (userId: string, updateData: Partial<UserModel>) => {
  try {
    const docRef = doc(usersCollection, userId);
    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error('Failed to update user:', error);
    throw error;
  }
};

// Delete user by ID
export const deleteUser = async (userId: string) => {
  try {
    const docRef = doc(usersCollection, userId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Failed to delete user:', error);
    throw error;
  }
};

// Bulk add (or subtract) wallet balance for many users, batched (max 500/commit).
// Uses FieldValue.increment so concurrent app-side changes aren't clobbered.
export const bulkAddWallet = async (
  userIds: string[],
  amount: number,
  onProgress?: (done: number, total: number) => void,
): Promise<number> => {
  const total = userIds.length;
  let done = 0;
  for (let i = 0; i < userIds.length; i += 400) {
    const slice = userIds.slice(i, i + 400);
    const batch = writeBatch(db);
    slice.forEach(id => batch.update(doc(usersCollection, id), { walletBalance: increment(amount) }));
    await batch.commit();
    done += slice.length;
    onProgress?.(done, total);
  }
  return done;
};

// List all users
export const fetchUsers = async (): Promise<UserModel[]> => {
  try {
    const snapshot = await getDocs(usersCollection);
    return snapshot.docs.map(doc => ({ ...(doc.data() as UserModel), userId: doc.id }));
  } catch (error) {
    console.error('Failed to fetch users:', error);
    return [];
  }
};

export {}; // export empty to mark module
